// #3248 PR 2 — OAuth keeps ?redirect=.
//
// /login?redirect=<deep link> (set by ProtectedRoute, or by a magic-link page)
// is lost when the user signs in with Google/GitHub: the browser leaves the SPA
// for the provider and comes back on /auth/callback. OAuthButtons stores the
// redirect here before leaving; OAuthCallbackPage takes it back. Both sides go
// through isSafeRedirect: an unsafe value is never stored, and a planted one is
// never followed. sessionStorage is per-tab, so another tab's sign-in can't
// pick it up.

import { isSafeRedirect } from './redirectGuard';

export const OAUTH_REDIRECT_KEY = 'bp_oauth_redirect';

/** Before the provider round trip: keep `?redirect=` from `search` when safe; otherwise clear any stale one. */
export function rememberOAuthRedirect(search: string | null | undefined, store: Pick<Storage, 'setItem' | 'removeItem'> = sessionStorage): void {
  try {
    const redirect = new URLSearchParams(search ?? '').get('redirect');
    if (isSafeRedirect(redirect)) store.setItem(OAUTH_REDIRECT_KEY, redirect);
    else store.removeItem(OAUTH_REDIRECT_KEY);
  } catch {
    // Storage unavailable (private mode, quota): sign-in still works, the deep link is just lost.
  }
}

/** After the round trip: return the stored redirect when safe (else null), and clear it either way. */
export function takeOAuthRedirect(store: Pick<Storage, 'getItem' | 'removeItem'> = sessionStorage): string | null {
  try {
    const value = store.getItem(OAUTH_REDIRECT_KEY);
    store.removeItem(OAUTH_REDIRECT_KEY);
    return isSafeRedirect(value) ? value : null;
  } catch {
    return null;
  }
}
